import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import hero1 from "../../assets/hero-img-1.jpg";
import hero2 from "../../assets/hero-img-2.jpg";
import hero3 from "../../assets/hero-img-3.jpg";
import programImg from "../../assets/program-img.jpg";
import students from "../../assets/studentsInLibrary.jpg";
import network from "../../assets/network.jpg";
import office from "../../assets/office.jpeg";
import graduators from "../../assets/graduators.jpg";
import { getNewsList } from "../../data/news";

const timeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 172800) return "Yesterday";
  return date.toLocaleDateString();
};

const fallbackNews = [
  {
    id: 101,
    slug: "jr-institute-ranked-top-tech-innovation-2025",
    title: "JR Institute Ranked Top for Tech Innovation in 2025",
    excerpt:
      "Our institute has been recognized for excellence in applied research and industry-ready curriculum.",
    description:
      "Our institute has been recognized for excellence in applied research and industry-ready curriculum.",
    content:
      "JR Institute has been ranked among the top institutions for technology innovation in 2025, citing groundbreaking research and robust industry partnerships.",
    priority: "breaking",
    category: "academic",
    tags: ["innovation", "development"],
    publishedAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    date: "24 Sep 2025",
    author: "Editorial Board",
    readTime: "5 min read",
    featuredImage: "/hero-img-3.jpg",
    isPinned: true,
    featured: true,
  },
  {
    id: 102,
    slug: "admissions-open-short-courses-oct-session",
    title: "Admissions Open: Short Courses Intake (Oct Session)",
    excerpt:
      "Enroll now for industry-driven short courses. Flexible schedules and certification included.",
    description:
      "Enroll now for industry-driven short courses. Flexible schedules and certification included.",
    content:
      "Our short courses are designed with input from industry leaders to ensure job-ready skills.",
    priority: "urgent",
    category: "academic",
    tags: ["innovation", "development"],
    publishedAt: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
    date: "23 Sep 2025",
    author: "Admissions Office",
    readTime: "3 min read",
    featuredImage: "/program-img1.jpg",
    isPinned: false,
    featured: true,
  },
  {
    id: 103,
    slug: "cybersecurity-bootcamp-final-call",
    title: "Cybersecurity Bootcamp: Final Call for Applications",
    excerpt:
      "Hands-on bootcamp led by industry experts. Limited seats available.",
    description:
      "Hands-on bootcamp led by industry experts. Limited seats available.",
    content:
      "A comprehensive, practical bootcamp covering network security, ethical hacking, and forensics.",
    priority: "update",
    category: "technology",
    tags: ["innovation", "partnership"],
    publishedAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    date: "24 Sep 2025",
    author: "Cybersecurity Dept.",
    readTime: "4 min read",
    featuredImage: "/cybersecurity.jpg",
    isPinned: false,
    featured: false,
  },
  {
    id: 104,
    slug: "scholarship-interviews-next-week",
    title: "Scholarship Interviews Scheduled for Next Week",
    excerpt:
      "Eligible applicants will receive email notifications with interview slots.",
    description:
      "Eligible applicants will receive email notifications with interview slots.",
    content:
      "Interviews will be conducted by a panel comprising faculty and industry partners.",
    priority: "normal",
    category: "achievements",
    tags: ["success", "community"],
    publishedAt: new Date(Date.now() - 1000 * 60 * 60 * 26).toISOString(),
    date: "23 Sep 2025",
    author: "Scholarship Committee",
    readTime: "6 min read",
    featuredImage: "/studentsInLibrary.jpg",
    isPinned: false,
    featured: false,
  },
];

const Badge = ({ priority }) => {
  const map = {
    breaking: "bg-red-600 text-white",
    urgent: "bg-amber-500 text-white",
    update: "bg-blue-600 text-white",
    announcement: "bg-emerald-600 text-white",
    normal: "bg-gray-300 text-gray-800",
  };
  const label =
    priority === "breaking"
      ? "Breaking"
      : priority.charAt(0).toUpperCase() + priority.slice(1);
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-semibold ${map[priority] || map.normal}`}
    >
      {label}
    </span>
  );
};

const ShareButton = ({ url, title }) => {
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard");
      }
    } catch {}
  };
  return (
    <button
      onClick={share}
      className="text-sm text-white/90 hover:text-white transition-colors"
    >
      Share
    </button>
  );
};

const SkeletonCard = () => (
  <div className="rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 overflow-hidden animate-pulse">
    <div className="h-40 sm:h-48 bg-white/10" />
    <div className="p-4">
      <div className="h-4 bg-white/10 rounded w-2/3 mb-2" />
      <div className="h-3 bg-white/10 rounded w-full mb-1.5" />
      <div className="h-3 bg-white/10 rounded w-5/6" />
    </div>
  </div>
);

const BreakingNewsUpdates = React.memo(() => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tickerOpen, setTickerOpen] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  const prefersReducedMotion = useMemo(
    () =>
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  // Handle navigation to detail route
  const handleNewsClick = (newsId) => {
    if (!newsId) {
      console.warn("News identifier is missing; redirecting to /news");
      navigate("/news");
      return;
    }
    navigate(`/news/${newsId}`);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer centralized dataset; fallback to local if empty
        const source = getNewsList();
        const data = Array.isArray(source) && source.length > 0 ? source : fallbackNews;
        // Sort by most recent publishedAt when available
        const sorted = [...data].sort((a, b) => {
          const ta = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
          const tb = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
          return tb - ta;
        });
        const localThumbs = [
          hero1,
          hero2,
          hero3,
          programImg,
          students,
          network,
          office,
          graduators,
        ].filter(Boolean);
        const enriched = sorted.map((n, idx) => ({
          ...n,
          thumbnail:
            localThumbs.length > 0
              ? localThumbs[idx % localThumbs.length]
              : `https://source.unsplash.com/collection/190727/800x600?sig=${idx}&education,campus,students`,
        }));
        if (!cancelled) {
          setNews(enriched);
        }
      } catch (e) {
        setError("Failed to load news.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    if (paused) return;
    if (!news.length) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % news.length);
    }, 5000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [paused, news.length, prefersReducedMotion]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("bn-visible");
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const featured = news[activeIndex];
  const others = news.filter((_, i) => i !== activeIndex).slice(0, 3);

  return (
    <section
      ref={sectionRef}
      className="relative w-full py-10 sm:py-14 bg-gradient-to-br from-custom-pink via-purple-900 to-indigo-900 text-white overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        aria-hidden
      >
        <div className="absolute -top-16 -left-16 w-64 h-64 bg-gold rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-custom-pink rounded-full blur-3xl" />
      </div>

      {/* Top Ticker */}
      {tickerOpen && news.length > 0 && (
        <div className="w-full mb-6 px-4 sm:px-6 lg:px-10">
          <div className="relative rounded-xl bg-white backdrop-blur-md border border-gold overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 animate-pulse-glow" />
                <span className="text-sm font-bold text-black">
                  Breaking Ticker
                </span>
              </div>
              <button
                onClick={() => setTickerOpen(false)}
                className="text-sm text-black hover:text-black font-bold"
              >
                Hide
              </button>
            </div>
            {/* Separator line between title and ticker items */}
            <div className="border-t border-gold/60" />
            <div
              className="bn-ticker select-none"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div className="bn-ticker-track">
                {news.map((n) => (
                  <div key={n.id} className="bn-ticker-item">
                    <Badge priority={n.priority || "update"} />
                    <span className="mx-2 text-black">{n.title}</span>
                    <span className="text-black font-bold">
                      • {timeAgo(n.publishedAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {!tickerOpen && (
        <div className="w-full mb-6 px-4 sm:px-6 lg:px-10">
          <div className="flex justify-end">
            <button
              onClick={() => setTickerOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-black border border-gold hover:shadow transition"
            >
              Show Ticker
            </button>
          </div>
        </div>
      )}

      <div className="w-full px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-wide">
            Breaking News & Latest Updates
          </h2>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-gold to-yellow-500 text-black font-bold hover:shadow-lg transition-shadow text-xs sm:text-sm"
          >
            View All News
          </Link>
        </div>

        {/* Loading / Error / Empty States */}
        {loading && (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <SkeletonCard />
            </div>
            <SkeletonCard />
          </div>
        )}
        {!loading && error && (
          <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/10 p-6 text-white/90">
            {error}
          </div>
        )}
        {!loading && !error && news.length === 0 && (
          <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/10 p-6 text-white/90">
            No news available right now. Please check back later.
          </div>
        )}

        {!loading && !error && news.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {/* Featured Card */}
            <Link
              to={`/news/${featured?.id || ''}`}
              className="md:col-span-2 group rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 overflow-hidden relative hover:shadow-2xl hover:bg-white/15 transition-all cursor-pointer block"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight")
                  setActiveIndex((i) => (i + 1) % news.length);
                if (e.key === "ArrowLeft")
                  setActiveIndex((i) => (i - 1 + news.length) % news.length);
                if (e.key === "Enter") {
                  handleNewsClick(featured?.id);
                }
              }}
              aria-label={`Read full article: ${featured?.title}`}
            >
              <div className="relative h-72 sm:h-96 lg:h-[30rem] overflow-hidden">
                <img
                  src={
                    featured?.thumbnail ||
                    `https://source.unsplash.com/random/800x600?education,campus`
                  }
                  alt={featured?.title || "News image"}
                  className="w-full h-full object-cover bn-img-blur group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                {featured?.priority === "breaking" && (
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600 text-white text-xs font-bold animate-pulse-glow">
                      Breaking
                    </span>
                  </div>
                )}
              </div>
              <div className="p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Badge priority={featured?.priority || "update"} />
                  <span className="text-white/80 text-sm">
                    {timeAgo(featured?.publishedAt)}
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-extrabold leading-snug mb-2 group-hover:underline">
                  {featured?.title}
                </h3>
                <p className="text-white/85 mb-4">
                  {featured?.excerpt?.length > 180
                    ? featured.excerpt.slice(0, 180) + "…"
                    : featured?.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 group-hover:bg-white/25 transition-colors">
                    Read more
                  </span>
                  <ShareButton
                    url={`${window.location.origin}/news/${featured?.id || ''}`}
                    title={featured?.title}
                  />
                </div>
              </div>
            </Link>

            {/* Secondary Cards - Fixed vertical centering */}
            <div className="grid gap-6">
              {others.map((n, idx) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 overflow-hidden hover:bg-white/15 hover:shadow-lg transition-all cursor-pointer block group"
                  aria-label={`Read full article: ${n.title}`}
                >
                  <div className="flex h-full">
                    <div className="flex-shrink-0">
                      <img
                        src={
                          n.thumbnail ||
                          `https://source.unsplash.com/collection/190727/300x300?sig=${idx}&education`
                        }
                        alt={n.title}
                        className="w-28 h-28 sm:h-full sm:min-h-[112px] object-cover hidden sm:block group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    {/* Fixed: Added flex and justify-center to center content vertically */}
                    <div className="p-4 flex-1 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge priority={n.priority || "update"} />
                        {new Date().getTime() -
                          new Date(n.publishedAt).getTime() <
                          24 * 3600 * 1000 && (
                          <span className="px-2 py-0.5 rounded bg-emerald-500 text-white text-[10px] font-bold">
                            NEW
                          </span>
                        )}
                        <span className="text-white/70 text-xs ml-auto">
                          {timeAgo(n.publishedAt)}
                        </span>
                      </div>
                      <h4 className="font-semibold group-hover:underline block mb-1 text-sm sm:text-base leading-tight">
                        {n.title}
                      </h4>
                      <p className="text-white/80 text-sm line-clamp-2">
                        {n.excerpt}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
});

export default BreakingNewsUpdates;
